import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Header from '../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Textarea } from '../components/ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Checkbox } from '../components/ui/checkbox';
import {
  Boxes, Search, MapPin, Plus, Loader2, Trash2, ArrowRightLeft, Building2, Factory, History,
  Upload, Download, FileText, ClipboardList, Pencil, MoreHorizontal, Receipt,
} from 'lucide-react';
import { formatDate, cn } from '../lib/utils';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const SIDDHI = 'Siddhi Jewels';

// ─── Location badge ─────────────────────────────────────────────────────────
const LocationBadge = ({ location, className }) => {
  const isSiddhi = location === SIDDHI;
  const Icon = isSiddhi ? Building2 : Factory;
  return (
    <Badge
      className={cn(
        'gap-1 font-semibold border',
        isSiddhi
          ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300 border-emerald-300/60 hover:bg-emerald-100'
          : 'bg-orange-100 text-orange-800 dark:bg-orange-500/15 dark:text-orange-300 border-orange-300/60 hover:bg-orange-100',
        className,
      )}
      data-testid={`location-badge-${isSiddhi ? 'siddhi' : 'factory'}`}
    >
      <Icon className="h-3 w-3" />
      {location}
    </Badge>
  );
};

// ─── Move dialog ────────────────────────────────────────────────────────────
const MoveDialog = ({ mould, locations, open, onOpenChange, onMoved }) => {
  const [toLoc, setToLoc] = useState('');
  const [remark, setRemark] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { setToLoc(''); setRemark(''); }, [mould?.id]);

  const submit = async () => {
    if (!mould) return;
    if (!toLoc) return toast.error('Pick a new location');
    if (toLoc === mould.current_location) return toast.error(`Already at ${toLoc}`);
    setSaving(true);
    try {
      await axios.post(`${API}/moulds/${mould.id}/move`, { to_location: toLoc, remark: remark || null });
      toast.success(`Moved to ${toLoc}`);
      onOpenChange(false);
      onMoved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Move failed');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="move-dialog">
        <DialogHeader>
          <DialogTitle>Move Mould</DialogTitle>
          <DialogDescription>
            {mould ? `${mould.mould_number} · Part ${mould.part_number} of ${mould.total_parts}` : ''}
          </DialogDescription>
        </DialogHeader>
        {mould && (
          <div className="text-xs text-muted-foreground -mt-2 flex items-center gap-2">
            Currently at <LocationBadge location={mould.current_location} />
          </div>
        )}
        <div className="space-y-3">
          <div>
            <Label className="text-xs">New Location</Label>
            <Select value={toLoc} onValueChange={setToLoc}>
              <SelectTrigger data-testid="move-location-select">
                <SelectValue placeholder="Pick destination…" />
              </SelectTrigger>
              <SelectContent>
                {locations.map(loc => (
                  <SelectItem key={loc} value={loc} disabled={mould?.current_location === loc}>
                    {loc} {mould?.current_location === loc ? '(current)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Remark (optional)</Label>
            <Textarea rows={2} value={remark} onChange={(e) => setRemark(e.target.value)} placeholder="e.g. Sent for new casting batch" data-testid="move-remark-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="move-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Move'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Create dialog ──────────────────────────────────────────────────────────
const CreateMouldDialog = ({ locations, open, onOpenChange, onCreated }) => {
  const [mouldNumber, setMouldNumber] = useState('');
  const [totalParts, setTotalParts] = useState(1);
  const [partNumber, setPartNumber] = useState(1);
  const [styleNumbers, setStyleNumbers] = useState('');
  const [location, setLocation] = useState(SIDDHI);
  const [remarks, setRemarks] = useState('');
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setMouldNumber(''); setTotalParts(1); setPartNumber(1);
    setStyleNumbers(''); setLocation(SIDDHI); setRemarks('');
  };

  const submit = async () => {
    if (!mouldNumber.trim()) return toast.error('Mould number is required');
    if (partNumber < 1 || partNumber > totalParts) return toast.error('Part number must be 1..total');
    setSaving(true);
    try {
      await axios.post(`${API}/moulds`, {
        mould_number: mouldNumber.trim(),
        part_number: Number(partNumber),
        total_parts: Number(totalParts),
        current_location: location,
        remarks: remarks.trim() || null,
        style_numbers: styleNumbers.split(',').map(s => s.trim()).filter(Boolean),
      });
      toast.success('Mould added');
      reset();
      onOpenChange(false);
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="create-mould-dialog">
        <DialogHeader>
          <DialogTitle>Add Mould Part</DialogTitle>
          <DialogDescription>Create one row per mould part. Multi-part moulds need one row each.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Mould Number</Label>
            <Input value={mouldNumber} onChange={(e) => setMouldNumber(e.target.value)} placeholder="e.g. M1001" data-testid="create-mould-number" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Part Number</Label>
              <Input type="number" min={1} value={partNumber} onChange={(e) => setPartNumber(e.target.value)} data-testid="create-part-number" />
            </div>
            <div>
              <Label className="text-xs">Total Parts</Label>
              <Input type="number" min={1} value={totalParts} onChange={(e) => setTotalParts(e.target.value)} data-testid="create-total-parts" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Used in Styles (comma-separated)</Label>
            <Input value={styleNumbers} onChange={(e) => setStyleNumbers(e.target.value)} placeholder="R1001, R2050, P3001" data-testid="create-style-numbers" />
          </div>
          <div>
            <Label className="text-xs">Current Location</Label>
            <Select value={location} onValueChange={setLocation}>
              <SelectTrigger data-testid="create-location-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {locations.map(loc => <SelectItem key={loc} value={loc}>{loc}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Remarks (optional)</Label>
            <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="create-remarks" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="create-mould-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Quick Lookup card (combines search-by-mould + search-by-style) ─────────
// Same features as before, half the footprint. Segmented control switches
// between the two summary views without adding a heavy new card.
const QuickLookupCard = () => {
  const [mode, setMode] = useState('mould'); // 'mould' | 'style'
  const [q, setQ] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { setQ(''); setResult(null); }, [mode]);

  const search = useCallback(async () => {
    const term = q.trim();
    if (!term) return;
    setLoading(true);
    try {
      const url = mode === 'mould'
        ? `${API}/moulds/by-mould-number/${encodeURIComponent(term)}`
        : `${API}/moulds/by-style/${encodeURIComponent(term)}`;
      const r = await axios.get(url);
      setResult({ mode, data: r.data });
    } catch (err) {
      setResult(null);
      if (err?.response?.status === 404) toast.error(`No ${mode === 'mould' ? 'mould' : 'style'} '${term}' found`);
      else toast.error(err?.response?.data?.detail || 'Search failed');
    } finally { setLoading(false); }
  }, [q, mode]);

  return (
    <Card data-testid="quick-lookup-card">
      <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
        <CardTitle className="font-heading text-base flex items-center gap-2">
          <Search className="h-4 w-4 text-blue-500" /> Quick Lookup
        </CardTitle>
        <div className="inline-flex rounded-md border p-0.5 bg-muted/50 text-xs">
          <button
            type="button"
            onClick={() => setMode('mould')}
            className={cn('px-2.5 py-1 rounded font-medium transition',
              mode === 'mould' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground')}
            data-testid="lookup-mode-mould"
          >
            By Mould
          </button>
          <button
            type="button"
            onClick={() => setMode('style')}
            className={cn('px-2.5 py-1 rounded font-medium transition',
              mode === 'style' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground')}
            data-testid="lookup-mode-style"
          >
            By Style
          </button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Input
            placeholder={mode === 'mould' ? 'Enter Mould Number (e.g. M1001)' : 'Enter Style Number (e.g. SPT01025A)'}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && search()}
            data-testid={mode === 'mould' ? 'search-by-mould-input' : 'search-by-style-input'}
          />
          <Button onClick={search} disabled={loading} data-testid={mode === 'mould' ? 'search-by-mould-btn' : 'search-by-style-btn'}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          </Button>
        </div>

        {result?.mode === 'mould' && result.data && (
          <div className="rounded-lg border bg-muted/30 p-3 space-y-2" data-testid="search-by-mould-result">
            <p className="font-heading font-bold text-lg">{result.data.mould_number}</p>
            <div className="space-y-1">
              {result.data.parts.map(p => (
                <div key={p.id} className="flex items-center gap-2 text-sm" data-testid={`result-part-${p.part_number}`}>
                  <span className="font-mono text-foreground">Part {p.part_number} of {p.total_parts}</span>
                  <span className="text-muted-foreground">→</span>
                  <LocationBadge location={p.current_location} />
                  {p.remarks && <span className="text-xs text-muted-foreground italic">— {p.remarks}</span>}
                </div>
              ))}
            </div>
            {result.data.styles_used_in.length > 0 && (
              <div className="pt-2 border-t border-border/50">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Used In</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {result.data.styles_used_in.map(s => (
                    <Badge key={s} variant="outline" className="text-xs font-mono">{s}</Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {result?.mode === 'style' && result.data && (
          <div className="rounded-lg border bg-muted/30 p-3 space-y-2" data-testid="search-by-style-result">
            <p className="font-heading font-bold text-lg">{result.data.style_number}</p>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Required Moulds ({result.data.count})</p>
            {result.data.count === 0 ? (
              <p className="text-xs text-muted-foreground italic">No moulds linked to this style yet.</p>
            ) : (
              <div className="space-y-1">
                {result.data.moulds.map(m => (
                  <div key={m.id} className="flex items-center gap-2 text-sm" data-testid={`style-result-mould-${m.id}`}>
                    <span className="font-mono font-semibold">{m.mould_number}</span>
                    <span className="text-muted-foreground">Part {m.part_number} of {m.total_parts}</span>
                    <span className="text-muted-foreground">→</span>
                    <LocationBadge location={m.current_location} />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ─── Edit dialog ────────────────────────────────────────────────────────────
const EditMouldDialog = ({ mould, open, onOpenChange, onSaved }) => {
  const [mouldNumber, setMouldNumber] = useState('');
  const [totalParts, setTotalParts] = useState(1);
  const [partNumber, setPartNumber] = useState(1);
  const [styleNumbers, setStyleNumbers] = useState('');
  const [remarks, setRemarks] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (mould) {
      setMouldNumber(mould.mould_number || '');
      setTotalParts(mould.total_parts || 1);
      setPartNumber(mould.part_number || 1);
      setStyleNumbers((mould.style_numbers || []).join(', '));
      setRemarks(mould.remarks || '');
    }
  }, [mould?.id]);

  const submit = async () => {
    if (!mould) return;
    if (!mouldNumber.trim()) return toast.error('Mould number is required');
    if (Number(partNumber) < 1 || Number(partNumber) > Number(totalParts)) return toast.error('Part number must be 1..total');
    setSaving(true);
    try {
      await axios.patch(`${API}/moulds/${mould.id}`, {
        mould_number: mouldNumber.trim(),
        part_number: Number(partNumber),
        total_parts: Number(totalParts),
        remarks: remarks.trim() || null,
        style_numbers: styleNumbers.split(',').map(s => s.trim()).filter(Boolean),
      });
      toast.success('Mould updated');
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="edit-mould-dialog">
        <DialogHeader>
          <DialogTitle>Edit Mould Part</DialogTitle>
          <DialogDescription>
            Location is changed via the Move button (this preserves the audit log).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Mould Number</Label>
            <Input value={mouldNumber} onChange={(e) => setMouldNumber(e.target.value)} data-testid="edit-mould-number" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Part Number</Label>
              <Input type="number" min={1} value={partNumber} onChange={(e) => setPartNumber(e.target.value)} data-testid="edit-part-number" />
            </div>
            <div>
              <Label className="text-xs">Total Parts</Label>
              <Input type="number" min={1} value={totalParts} onChange={(e) => setTotalParts(e.target.value)} data-testid="edit-total-parts" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Used in Styles (comma-separated)</Label>
            <Input value={styleNumbers} onChange={(e) => setStyleNumbers(e.target.value)} placeholder="R1001, R2050" data-testid="edit-style-numbers" />
          </div>
          <div>
            <Label className="text-xs">Remarks</Label>
            <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="edit-remarks" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="edit-mould-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Bulk Import dialog ─────────────────────────────────────────────────────
const ImportDialog = ({ open, onOpenChange, onDone }) => {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = async () => {
    try {
      const r = await axios.get(`${API}/moulds/import-template`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'mould_import_template.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Could not download template');
    }
  };

  const submit = async () => {
    if (!file) return toast.error('Choose a file first');
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/moulds/import`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(r.data);
      toast.success(`Imported ${r.data.created}. Skipped ${r.data.skipped}. Errors ${r.data.errors}.`);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { setFile(null); setResult(null); } onOpenChange(o); }}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle>Bulk Import Moulds</DialogTitle>
          <DialogDescription>Upload .csv or .xlsx using the strict template. Duplicate (Mould+Part) rows are skipped.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Button variant="outline" onClick={downloadTemplate} className="w-full" data-testid="import-download-template">
            <Download className="h-4 w-4 mr-2" /> Download Template (CSV)
          </Button>
          <div>
            <Label className="text-xs">File (.csv / .xlsx)</Label>
            <Input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="import-file-input"
            />
          </div>
          {result && (
            <div className="rounded-lg border bg-muted/40 p-3 space-y-2 text-xs" data-testid="import-result">
              <div className="flex gap-3 text-sm">
                <span className="font-bold text-emerald-700 dark:text-emerald-400">Created {result.created}</span>
                <span className="font-bold text-amber-700 dark:text-amber-400">Skipped {result.skipped}</span>
                <span className="font-bold text-rose-700 dark:text-rose-400">Errors {result.errors}</span>
              </div>
              {result.skipped > 0 && (
                <details>
                  <summary className="cursor-pointer text-amber-700 dark:text-amber-400">Skipped rows</summary>
                  <ul className="list-disc ml-5 mt-1">
                    {result.skipped_rows.map((s, i) => (
                      <li key={i}>Row {s.row}: {s.mould_number} part {s.part_number} — {s.reason}</li>
                    ))}
                  </ul>
                </details>
              )}
              {result.errors > 0 && (
                <details>
                  <summary className="cursor-pointer text-rose-700 dark:text-rose-400">Errors</summary>
                  <ul className="list-disc ml-5 mt-1">
                    {result.error_rows.map((e, i) => (
                      <li key={i}>Row {e.row}: {e.error}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
          <Button onClick={submit} disabled={busy || !file} data-testid="import-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Import'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Status Report card (paste lists) ───────────────────────────────────────
const StatusReportCard = () => {
  const [mouldText, setMouldText] = useState('');
  const [styleText, setStyleText] = useState('');
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);

  const split = (text) => Array.from(new Set(
    text.split(/\r?\n/).map(s => s.trim()).filter(Boolean)
  ));

  const run = async () => {
    const mould_numbers = split(mouldText);
    const style_numbers = split(styleText);
    if (!mould_numbers.length && !style_numbers.length) return toast.error('Paste at least one value');
    setBusy(true);
    try {
      const r = await axios.post(`${API}/moulds/status-report`, { mould_numbers, style_numbers });
      setReport(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  const downloadCsv = async () => {
    const mould_numbers = split(mouldText);
    const style_numbers = split(styleText);
    if (!report) return;
    try {
      const r = await axios.post(`${API}/moulds/status-report.csv`, { mould_numbers, style_numbers }, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mould_status_report_${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Download failed');
    }
  };

  return (
    <Card data-testid="status-report-card">
      <CardHeader className="pb-2">
        <CardTitle className="font-heading text-sm flex items-center gap-2">
          <ClipboardList className="h-4 w-4 text-violet-500" /> Status Report
          <span className="text-[10px] font-normal text-muted-foreground">Paste mould / style numbers, one per line</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 pt-0">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <Textarea
            rows={3}
            value={mouldText}
            onChange={(e) => setMouldText(e.target.value)}
            placeholder={'Mould Numbers\nM1001\nM2050'}
            className="font-mono text-xs resize-none"
            data-testid="report-mould-textarea"
          />
          <Textarea
            rows={3}
            value={styleText}
            onChange={(e) => setStyleText(e.target.value)}
            placeholder={'Style Numbers\nR1001\nR2050'}
            className="font-mono text-xs resize-none"
            data-testid="report-style-textarea"
          />
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={run} disabled={busy} data-testid="report-run-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Search className="h-3.5 w-3.5 mr-1" /> Run Report</>}
          </Button>
          {report && (
            <Button size="sm" variant="outline" onClick={downloadCsv} data-testid="report-download-btn">
              <Download className="h-3.5 w-3.5 mr-1" /> CSV
            </Button>
          )}
        </div>
        {report && (
          <div className="border rounded-lg overflow-hidden" data-testid="report-result">
            {report.count === 0 ? (
              <p className="text-center text-muted-foreground py-4 text-xs">No moulds matched.</p>
            ) : (
              <div className="overflow-x-auto max-h-[300px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Mould</TableHead>
                      <TableHead>Part</TableHead>
                      <TableHead>Style</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Remarks</TableHead>
                      <TableHead>Last Moved</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {report.rows.map((r, i) => (
                      <TableRow key={`${r.mould_id}-${i}`} data-testid={`report-row-${i}`}>
                        <TableCell className="font-mono font-bold">{r.mould_number}</TableCell>
                        <TableCell className="font-mono text-xs">{r.part_number} of {r.total_parts}</TableCell>
                        <TableCell className="font-mono text-xs">{r.style_number || '—'}</TableCell>
                        <TableCell><LocationBadge location={r.current_location} /></TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-[140px] truncate">{r.remarks || '—'}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{r.last_moved_at ? formatDate(r.last_moved_at) : '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            {(report.not_found.mould_numbers.length > 0 || report.not_found.style_numbers.length > 0) && (
              <div className="px-3 py-2 border-t bg-amber-50/40 dark:bg-amber-500/5 text-xs">
                <span className="font-semibold text-amber-800 dark:text-amber-300">Not found: </span>
                {report.not_found.mould_numbers.map(m => <Badge key={m} variant="outline" className="text-[10px] mr-1">{m}</Badge>)}
                {report.not_found.style_numbers.map(s => <Badge key={s} variant="outline" className="text-[10px] mr-1">{s}</Badge>)}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ─── Issue Mould Voucher Dialog ─────────────────────────────────────────────
// Generates a signed voucher PDF listing every mould moved to the selected
// factory within a date range. Persists the record so it can be re-downloaded.
const IssueVoucherDialog = ({ open, onOpenChange, locations }) => {
  // Default range = last 7 days ending today
  const today = new Date().toISOString().slice(0, 10);
  const weekAgo = new Date(Date.now() - 6 * 24 * 3600 * 1000).toISOString().slice(0, 10);
  const [form, setForm] = useState({ to_location: '', from_date: weekAgo, to_date: today, notes: '' });
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [past, setPast] = useState([]);
  const factoryLocations = locations.filter(l => l !== SIDDHI);

  // Reset & load past vouchers when opened
  useEffect(() => {
    if (!open) return;
    setForm(f => ({ ...f, to_location: '', notes: '' }));
    setPreview(null);
    axios.get(`${API}/mould-vouchers`).then(r => setPast(r.data || [])).catch(() => setPast([]));
  }, [open]);

  // Debounced preview count as inputs change
  useEffect(() => {
    if (!open || !form.to_location) { setPreview(null); return; }
    let cancelled = false;
    setPreviewLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/mould-vouchers/preview`, {
          params: { to_location: form.to_location, from_date: form.from_date, to_date: form.to_date },
        });
        if (!cancelled) setPreview(r.data);
      } catch {
        if (!cancelled) setPreview(null);
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, form.to_location, form.from_date, form.to_date]);

  const generate = async () => {
    if (!form.to_location) { toast.error('Pick a factory'); return; }
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/mould-vouchers`, {
        to_location: form.to_location, from_date: form.from_date,
        to_date: form.to_date, notes: form.notes || '',
      }, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = r.headers['content-disposition'] || '';
      const match = cd.match(/filename="?([^";]+)"?/i);
      a.download = match ? match[1] : `voucher_${Date.now()}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Voucher ${r.headers['x-voucher-number'] || ''} downloaded`);
      // Refresh the past list
      const list = await axios.get(`${API}/mould-vouchers`);
      setPast(list.data || []);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 404) {
        toast.error(detail || 'No moulds were moved to this factory in that range');
      } else {
        toast.error(detail || 'Failed to generate voucher');
      }
    } finally { setGenerating(false); }
  };

  const redownload = async (vid) => {
    try {
      const r = await axios.get(`${API}/mould-vouchers/${vid}/pdf`, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = r.headers['content-disposition'] || '';
      const match = cd.match(/filename="?([^";]+)"?/i);
      a.download = match ? match[1] : `voucher_${vid}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download voucher');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="issue-voucher-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5 text-amber-600" /> Issue Mould Voucher
          </DialogTitle>
          <DialogDescription>
            Generate a signed IMV listing every mould moved to a factory within a date range.
            Same layout as the Chain PO document.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Factory *</Label>
              <Select value={form.to_location} onValueChange={(v) => setForm({ ...form, to_location: v })}>
                <SelectTrigger data-testid="voucher-factory-select">
                  <SelectValue placeholder="Pick a factory…" />
                </SelectTrigger>
                <SelectContent>
                  {factoryLocations.length === 0 && (
                    <SelectItem value="__none" disabled>No factory locations yet</SelectItem>
                  )}
                  {factoryLocations.map(loc => <SelectItem key={loc} value={loc}>{loc}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">From date *</Label>
              <Input type="date" value={form.from_date}
                onChange={(e) => setForm({ ...form, from_date: e.target.value })}
                data-testid="voucher-from-date"
              />
            </div>
            <div>
              <Label className="text-xs">To date *</Label>
              <Input type="date" value={form.to_date}
                onChange={(e) => setForm({ ...form, to_date: e.target.value })}
                data-testid="voucher-to-date"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Notes (printed on PDF)</Label>
            <Textarea rows={2} value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Optional — e.g. 'Batch for July casting run'"
              data-testid="voucher-notes"
            />
          </div>

          {/* Live preview count */}
          <div className="rounded-lg border overflow-hidden" data-testid="voucher-preview">
            <div className="px-3 py-2 bg-muted/60 border-b flex items-center justify-between text-xs">
              <span className="font-semibold flex items-center gap-1.5">
                <Search className="h-3.5 w-3.5" /> Preview
                {previewLoading && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
              </span>
              {preview ? (
                <span className={cn('font-mono', preview.count > 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-muted-foreground')}>
                  <b>{preview.count}</b> mould-move{preview.count === 1 ? '' : 's'} to <b>{preview.to_location}</b>
                </span>
              ) : (
                <span className="text-muted-foreground italic">Pick a factory to see the count</span>
              )}
            </div>
          </div>

          {/* Past vouchers */}
          {past.length > 0 && (
            <div className="rounded-lg border overflow-hidden">
              <div className="px-3 py-2 bg-muted/60 border-b text-xs font-semibold flex items-center gap-1.5">
                <History className="h-3.5 w-3.5" /> Past vouchers ({past.length})
              </div>
              <div className="max-h-56 overflow-y-auto divide-y">
                {past.slice(0, 30).map(v => (
                  <div key={v.id} className="px-3 py-2 flex items-center justify-between gap-2 text-xs" data-testid={`past-voucher-${v.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono font-semibold">{v.voucher_number}</span>
                        <Badge variant="outline" className="text-[10px]">{v.to_location}</Badge>
                        <span className="text-muted-foreground">{v.item_count} moulds</span>
                      </div>
                      <p className="text-[10px] text-muted-foreground">
                        Range {v.from_date} → {v.to_date} · issued {v.issued_at?.slice(0, 10)} by {v.issued_by_name}
                      </p>
                    </div>
                    <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => redownload(v.id)} data-testid={`redownload-voucher-${v.id}`}>
                      <Download className="h-3 w-3 mr-1" /> PDF
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={generating}>Cancel</Button>
          <Button
            onClick={generate}
            disabled={generating || !form.to_location || (preview && preview.count === 0)}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="generate-voucher-btn"
          >
            {generating ? <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Generating…</> : <><Download className="h-4 w-4 mr-1" /> Generate &amp; download</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Main page ──────────────────────────────────────────────────────────────
const MouldTracker = () => {
  const [moulds, setMoulds] = useState([]);
  const [locations, setLocations] = useState([SIDDHI]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [filterLocation, setFilterLocation] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [voucherOpen, setVoucherOpen] = useState(false);
  const [moveMould, setMoveMould] = useState(null);
  const [editMould, setEditMould] = useState(null);
  const [recent, setRecent] = useState([]);
  // Bulk selection state — set of mould.id strings currently checked in the table
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Load moulds via server-side search. `initial=true` shows skeleton;
  // subsequent search-as-you-type calls only flip `searching` so the table
  // stays visible with a tiny spinner overlay.
  const load = useCallback(async ({ initial = false } = {}) => {
    if (initial) setLoading(true); else setSearching(true);
    try {
      const params = {};
      if (filterText.trim()) params.q = filterText.trim();
      if (filterLocation !== 'all') params.location = filterLocation;
      const [m, rm, lc] = await Promise.all([
        axios.get(`${API}/moulds`, { params }),
        initial ? axios.get(`${API}/moulds/recent-movements?limit=20`) : Promise.resolve({ data: recent }),
        initial ? axios.get(`${API}/moulds/locations`) : Promise.resolve({ data: { locations } }),
      ]);
      setMoulds(m.data);
      if (initial) {
        setRecent(rm.data);
        setLocations(lc.data.locations);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load');
    } finally {
      if (initial) setLoading(false); else setSearching(false);
    }
  }, [filterText, filterLocation]);

  // Initial load (fires once on mount) — shows skeleton
  useEffect(() => {
    load({ initial: true });
  }, []);

  // Debounced server-side search-as-you-type. 300ms after the user stops
  // typing (or immediately when location changes), re-fetch from the server.
  // Skip on very first mount — initial load above handles that.
  const isFirstRender = React.useRef(true);
  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    const t = setTimeout(() => { load(); }, 300);
    return () => clearTimeout(t);
  }, [filterText, filterLocation]);

  const refresh = () => load({ initial: true });

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this mould part?')) return;
    try {
      await axios.delete(`${API}/moulds/${id}`);
      toast.success('Deleted');
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkDeleting(true);
    try {
      const r = await axios.post(`${API}/moulds/bulk-delete`, { ids });
      toast.success(`Deleted ${r.data.deleted} mould${r.data.deleted === 1 ? '' : 's'}`);
      setSelectedIds(new Set());
      setBulkDeleteConfirmOpen(false);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk delete failed');
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/moulds/export`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Pull filename from Content-Disposition if available
      const cd = res.headers['content-disposition'] || '';
      const match = cd.match(/filename="?([^";]+)"?/i);
      a.download = match ? match[1] : `moulds_export_${Date.now()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${moulds.length} mould${moulds.length === 1 ? '' : 's'}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold flex items-center gap-2">
              <Boxes className="h-6 w-6 text-blue-500" /> Mould Tracker
            </h1>
            <p className="text-xs text-muted-foreground">Locate moulds instantly. Search by mould or style.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => setCreateOpen(true)} data-testid="add-mould-btn">
              <Plus className="h-4 w-4 mr-1" /> Add Mould
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" data-testid="tools-menu-trigger" aria-label="More tools">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuItem onClick={handleExport} disabled={exporting} data-testid="export-btn">
                  {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                  Export to Excel
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setImportOpen(true)} data-testid="import-btn">
                  <Upload className="h-4 w-4 mr-2" /> Import from CSV
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setVoucherOpen(true)} data-testid="open-voucher-btn">
                  <Receipt className="h-4 w-4 mr-2" /> Issue Mould Voucher
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Quick lookup (combined mould / style) */}
        <QuickLookupCard />

        {/* Status Report (compact) */}
        <StatusReportCard />

        {/* Table */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <CardTitle className="font-heading text-base flex items-center gap-2">
                All Moulds ({moulds.length})
                {searching && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" data-testid="all-moulds-searching" />}
              </CardTitle>
              <div className="flex items-center gap-2">
                {selectedIds.size > 0 && (
                  <>
                    <span className="text-xs text-muted-foreground" data-testid="bulk-selected-count">
                      {selectedIds.size} selected
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 text-xs"
                      onClick={() => setSelectedIds(new Set())}
                      data-testid="bulk-clear-selection"
                    >
                      Clear
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      className="h-8 text-xs"
                      onClick={() => setBulkDeleteConfirmOpen(true)}
                      data-testid="bulk-delete-btn"
                    >
                      <Trash2 className="h-3 w-3 mr-1" /> Delete selected
                    </Button>
                  </>
                )}
                <div className="relative">
                  <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                  <Input
                    placeholder="Search mould or style…"
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                    className="w-64 h-8 pl-8"
                    data-testid="table-filter-input"
                  />
                </div>
                <Select value={filterLocation} onValueChange={setFilterLocation}>
                  <SelectTrigger className="w-40 h-8" data-testid="table-filter-location">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All locations</SelectItem>
                    {locations.map(loc => <SelectItem key={loc} value={loc}>{loc}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? <Skeleton className="h-40 w-full" /> : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox
                          checked={moulds.length > 0 && moulds.every(m => selectedIds.has(m.id))}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedIds(new Set(moulds.map(m => m.id)));
                            } else {
                              setSelectedIds(new Set());
                            }
                          }}
                          aria-label="Select all moulds"
                          data-testid="bulk-select-all"
                        />
                      </TableHead>
                      <TableHead>Mould</TableHead>
                      <TableHead>Part</TableHead>
                      <TableHead>Used In</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Remarks</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {moulds.length === 0 ? (
                      <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No moulds.</TableCell></TableRow>
                    ) : moulds.map(m => (
                      <TableRow key={m.id} data-testid={`mould-row-${m.id}`} data-state={selectedIds.has(m.id) ? 'selected' : undefined}>
                        <TableCell className="w-10">
                          <Checkbox
                            checked={selectedIds.has(m.id)}
                            onCheckedChange={() => toggleSelect(m.id)}
                            aria-label={`Select mould ${m.mould_number}`}
                            data-testid={`mould-select-${m.id}`}
                          />
                        </TableCell>
                        <TableCell className="font-mono font-bold">{m.mould_number}</TableCell>
                        <TableCell className="font-mono text-sm">{m.part_number} of {m.total_parts}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {(m.style_numbers || []).slice(0, 5).map(s => (
                              <Badge key={s} variant="outline" className="text-[10px] font-mono">{s}</Badge>
                            ))}
                            {(m.style_numbers || []).length > 5 && (
                              <Badge variant="secondary" className="text-[10px]">+{m.style_numbers.length - 5}</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell><LocationBadge location={m.current_location} /></TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-[180px] truncate" title={m.remarks || ''}>
                          {m.remarks || '—'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => setMoveMould(m)} data-testid={`move-btn-${m.id}`}>
                              <ArrowRightLeft className="h-3 w-3 mr-1" /> Move
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setEditMould(m)} data-testid={`edit-btn-${m.id}`}>
                              <Pencil className="h-3 w-3" />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-500" onClick={() => handleDelete(m.id)} data-testid={`delete-btn-${m.id}`}>
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recently moved */}
        <Card data-testid="recently-moved">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-base flex items-center gap-2">
              <History className="h-4 w-4 text-amber-500" /> Recently Moved Moulds
            </CardTitle>
            <p className="text-xs text-muted-foreground">Last 20 location changes</p>
          </CardHeader>
          <CardContent>
            {recent.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-4">No movements yet.</p>
            ) : (
              <div className="space-y-1.5">
                {recent.map(mv => (
                  <div key={mv.id} className="flex items-center justify-between gap-2 text-xs py-1.5 px-2 rounded hover:bg-muted/40">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-mono font-bold">{mv.mould_number}</span>
                      <span className="text-muted-foreground">Part {mv.part_number} of {mv.total_parts}</span>
                    </div>
                    <div className="flex items-center gap-1.5 min-w-0">
                      <LocationBadge location={mv.from_location || SIDDHI} className="text-[10px] py-0" />
                      <ArrowRightLeft className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                      <LocationBadge location={mv.to_location} className="text-[10px] py-0" />
                    </div>
                    <div className="text-[10px] text-muted-foreground text-right">
                      {mv.user_name} · {formatDate(mv.timestamp)}
                      {mv.remark && <div className="italic">“{mv.remark}”</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <CreateMouldDialog
          locations={locations}
          open={createOpen}
          onOpenChange={setCreateOpen}
          onCreated={refresh}
        />
        <ImportDialog
          open={importOpen}
          onOpenChange={setImportOpen}
          onDone={refresh}
        />
        <IssueVoucherDialog
          open={voucherOpen}
          onOpenChange={setVoucherOpen}
          locations={locations}
        />
        <MoveDialog
          mould={moveMould}
          locations={locations}
          open={!!moveMould}
          onOpenChange={(v) => !v && setMoveMould(null)}
          onMoved={refresh}
        />
        <EditMouldDialog
          mould={editMould}
          open={!!editMould}
          onOpenChange={(v) => !v && setEditMould(null)}
          onSaved={refresh}
        />

        {/* Bulk delete confirmation */}
        <Dialog open={bulkDeleteConfirmOpen} onOpenChange={(v) => { if (!bulkDeleting) setBulkDeleteConfirmOpen(v); }}>
          <DialogContent className="max-w-md" data-testid="bulk-delete-confirm-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-rose-600">
                <Trash2 className="h-5 w-5" /> Delete {selectedIds.size} mould{selectedIds.size === 1 ? '' : 's'}?
              </DialogTitle>
              <DialogDescription>
                This will permanently remove the selected mould part{selectedIds.size === 1 ? '' : 's'} from the tracker. This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-48 overflow-y-auto space-y-1 py-2">
              {moulds.filter(m => selectedIds.has(m.id)).slice(0, 20).map(m => (
                <div key={m.id} className="text-xs flex items-center gap-2 px-2 py-1 rounded bg-muted/40">
                  <span className="font-mono font-bold">{m.mould_number}</span>
                  <span className="text-muted-foreground">Part {m.part_number} of {m.total_parts}</span>
                  <LocationBadge location={m.current_location} className="text-[10px] py-0 ml-auto" />
                </div>
              ))}
              {selectedIds.size > 20 && (
                <p className="text-[11px] text-muted-foreground italic">…and {selectedIds.size - 20} more</p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBulkDeleteConfirmOpen(false)} disabled={bulkDeleting} data-testid="bulk-delete-cancel">
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleBulkDelete} disabled={bulkDeleting} data-testid="bulk-delete-confirm">
                {bulkDeleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
                Delete {selectedIds.size}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default MouldTracker;
